import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../data/store';
import { useNavigate } from 'react-router-dom';
import Button from './common/buttons/Button';
import Config from '../config';
import { styled } from 'styled-components';
import Paper from './common/layout/Paper';
import Link from './common/Link';
import OrbitoLogo from '../images/Orbito_Logo_WhiteText.svg';
import OpenInNewOutlinedIcon from '@mui/icons-material/OpenInNewOutlined';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import backgroundImage from '../images/Orbito-BG.png';

type AccountType = 'gmail' | 'workspace' | null;

export default function SignupScreen() {
	const navigate = useNavigate();
	const authState = useSelector((state: RootState) => state.authState);
	const [selectedAccountType, setSelectedAccountType] =
		useState<AccountType>(null);

	useEffect(() => {
		if (authState.astroToken) {
			navigate('/');
		}
	}, [authState, navigate]);

	const SCOPES =
		'email profile https://www.googleapis.com/auth/chat.spaces https://www.googleapis.com/auth/chat.messages https://www.googleapis.com/auth/chat.memberships https://www.googleapis.com/auth/admin.directory.user.readonly https://www.googleapis.com/auth/drive https://www.googleapis.com/auth/presentations https://www.googleapis.com/auth/spreadsheets https://www.googleapis.com/auth/documents';

	const handleSignup = () => {
		const state = encodeURIComponent(
			JSON.stringify({ isSignup: true, accountType: selectedAccountType })
		);
		const authUrl = `https://accounts.google.com/o/oauth2/v2/auth?response_type=code&client_id=${encodeURIComponent(
			Config.googleOAuth.GOOGLE_CLIENT_ID
		)}&redirect_uri=${encodeURIComponent(
			Config.googleOAuth.REDIRECT_URI
		)}&scope=${encodeURIComponent(
			SCOPES
		)}&state=${state}&access_type=offline&prompt=consent`;
		window.location.href = authUrl;
	};

	return (
		<Container>
			<ContentWrapper>
				<WorkspaceRequirementsBox>
					<Header>
						<HeaderText>Before you begin</HeaderText>
						<LogoContainer>
							<img src={OrbitoLogo} alt="Orbito Logo" />
						</LogoContainer>
					</Header>
					<SubHeaderText>
						First, let us know what type of google account you will
						be signing up with?
					</SubHeaderText>

					<AccountTypeSection>
						<AccountTypeButtonsContainer>
							<AccountTypeButton
								selected={selectedAccountType === 'gmail'}
								onClick={() => setSelectedAccountType('gmail')}
							>
								<PersonOutlineIcon
									style={{
										fontSize: 28,
										color:
											selectedAccountType === 'gmail'
												? '#4285F4'
												: undefined,
									}}
								/>
								<AccountTypeButtonText>
									Gmail Account
								</AccountTypeButtonText>
							</AccountTypeButton>

							<AccountTypeButton
								selected={selectedAccountType === 'workspace'}
								onClick={() =>
									setSelectedAccountType('workspace')
								}
							>
								<GroupsOutlinedIcon
									style={{
										fontSize: 28,
										color:
											selectedAccountType === 'workspace'
												? '#4285F4'
												: undefined,
									}}
								/>
								<AccountTypeButtonText>
									Workspace Account
								</AccountTypeButtonText>
							</AccountTypeButton>
						</AccountTypeButtonsContainer>

						{selectedAccountType && (
							<AccountTypeDescription>
								<AccountTypeTitle>
									{selectedAccountType === 'gmail'
										? 'Individual Account'
										: 'Workspace Account'}
								</AccountTypeTitle>
								<DescriptionContent>
									{selectedAccountType === 'gmail' ? (
										<>
											<DescriptionSection>
												<DescriptionSubheading>
													Free & Personal Email
												</DescriptionSubheading>
												<DescriptionText>
													It is a free service that
													provides a personal email
													address ending in
													@gmail.com.
												</DescriptionText>
											</DescriptionSection>
											<DescriptionSection>
												<DescriptionSubheading>
													Individual Ownership
												</DescriptionSubheading>
												<DescriptionText>
													The account is owned and
													managed entirely by the
													individual user, with no
													ability for centralized
													management or recovery by an
													outside entity.
												</DescriptionText>
											</DescriptionSection>
											<DescriptionSection>
												<DescriptionSubheading>
													Basic Toolset & Storage
												</DescriptionSubheading>
												<DescriptionText>
													Includes 15 GB of free
													storage shared across Gmail,
													Drive, and Photos, and
													access to the basic versions
													of core Google apps (Docs,
													Sheets, Slides).
												</DescriptionText>
											</DescriptionSection>
											<CreateAccountPrompt>
												Don't have a Gmail account?
												<CreateAccountLink
													onClick={() =>
														window.open(
															'https://gmail.com',
															'_blank'
														)
													}
												>
													CREATE GMAIL ACCOUNT
												</CreateAccountLink>
											</CreateAccountPrompt>
										</>
									) : (
										<>
											<DescriptionSection>
												<DescriptionSubheading>
													Professional Domain
												</DescriptionSubheading>
												<DescriptionText>
													Provides a custom business
													email address using your
													company's domain (e.g.,
													name@yourcompany.com) to
													establish professionalism
													and brand identity.
												</DescriptionText>
											</DescriptionSection>
											<DescriptionSection>
												<DescriptionSubheading>
													Centralized Admin Control
												</DescriptionSubheading>
												<DescriptionText>
													Includes the Admin Console
													for IT administrators to
													centrally manage all users,
													data, security policies, and
													organizational resources.
												</DescriptionText>
											</DescriptionSection>
											<DescriptionSection>
												<DescriptionSubheading>
													Integrated Team
													Collaboration
												</DescriptionSubheading>
												<DescriptionText>
													Offers advanced, integrated
													tools like shared drives,
													shared calendars, and
													enhanced versions of Meet
													and Chat specifically
													designed for real-time
													team-wide collaboration.
												</DescriptionText>
											</DescriptionSection>
											<CreateAccountPrompt>
												Don't have a Workspace account?
												<CreateAccountLink
													onClick={() =>
														window.open(
															'https://workspace.google.com',
															'_blank'
														)
													}
												>
													CREATE WORKSPACE ACCOUNT
												</CreateAccountLink>
											</CreateAccountPrompt>
										</>
									)}
								</DescriptionContent>
							</AccountTypeDescription>
						)}
					</AccountTypeSection>

					{/* <RequirementsList>
						<RequirementItem>
							You must have an existing Google Workspace account that
							Orbito can link to.
						</RequirementItem>
						<RequirementItem>
							Your linked Google Workspace will contain all project
							files and folders created by Orbito.
						</RequirementItem>
						<RequirementItem>
							If you do not have a Google Workspace account, create
							one first before creating an account by visiting{' '}
							<Link
								onClick={() =>
									window.open(
										'https://workspace.google.com',
										'_blank'
									)
								}
							>
								workspace.google.com
							</Link>
						</RequirementItem>
					</RequirementsList> */}
					<Button
						style={{
							width: '100%',
							background:
								'linear-gradient(101deg, #4285F4 3.32%, #EA4335 63.03%, #FBBC04 122.74%)',
						}}
						onClick={handleSignup}
					>
						Continue
					</Button>
				</WorkspaceRequirementsBox>
			</ContentWrapper>
			<BottomSection>
				<Link
					style={{
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'center',
						fontSize: 12,
					}}
					onClick={() =>
						window.open(
							`${Config.baseUrl}/privacy-policy`,
							'_blank'
						)
					}
				>
					View our Privacy Policy <StyledOpenInNewOutlinedIcon />
				</Link>
				<BottomText>
					Orbito is a state of the art web based platform utilizing
					best in class film production workflows all powered through
					Google Workspace APIs. No user data obtained through Google
					APIs are used to develop, improve or train generalized AI
					and/or ML tools. No data is shared or sold to any external
					entity.
				</BottomText>
			</BottomSection>
		</Container>
	);
}

const Container = styled.div`
	display: flex;
	flex-direction: column;
	min-height: 100vh;
	width: 100vw;
	position: relative;
	overflow-y: auto;
	&::before {
		content: '';
		position: fixed;
		top: 0;
		left: 0;
		width: 100%;
		height: 100%;
		z-index: -1;
		background-image: url(${backgroundImage});
		background-size: cover;
		background-position: center;
		background-repeat: no-repeat;
		background-attachment: fixed;
	}
`;

const ContentWrapper = styled.div`
	display: flex;
	align-items: center;
	justify-content: center;
	flex: 1;
	min-height: 0;
	padding: ${({ theme }) => theme.spacing(4)} 0;
`;

const BottomSection = styled.div`
	position: sticky;
	bottom: 15px;
	width: 100%;
	margin-top: auto;
	padding-top: ${({ theme }) => theme.spacing(2)};
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: ${({ theme }) => theme.spacing(1)};
`;

const WorkspaceRequirementsBox = styled(Paper)`
	width: 500px;
	padding: ${({ theme }) => theme.spacing(4)};
`;

const Header = styled.div`
	display: flex;
	justify-content: space-between;
	align-items: center;
	margin-bottom: ${({ theme }) => theme.spacing(4)};
`;

const HeaderText = styled.div`
	font-size: 32px;
	color: ${({ theme }) => theme.palette.text.label};
`;

const SubHeaderText = styled.div`
	font-size: 14px;
	color: ${({ theme }) => theme.palette.text.secondary};
	margin-bottom: ${({ theme }) => theme.spacing(2)};
`;

const LogoContainer = styled.div`
	display: flex;
	align-items: center;
	gap: ${({ theme }) => theme.spacing(1)};
`;

const LogoText = styled.div`
	font-size: 14px;
	font-weight: 600;
	color: ${({ theme }) => theme.palette.text.primary};
`;

const RequirementsList = styled.div`
	display: flex;
	flex-direction: column;
	gap: ${({ theme }) => theme.spacing(3)};
	margin-bottom: ${({ theme }) => theme.spacing(4)};
`;

const RequirementItem = styled.div`
	color: ${({ theme }) => theme.palette.text.primary};
	font-size: 14px;
	line-height: 1.5;
`;

const StyledOpenInNewOutlinedIcon = styled(OpenInNewOutlinedIcon)`
	margin-left: ${({ theme }) => theme.spacing(1)};
	font-size: 14px !important;
`;

const BottomText = styled.div`
	display: flex;
	align-items: center;
	justify-content: center;
	font-size: 12px;
	padding-left: 100px;
	padding-right: 100px;
	text-align: center;
	color: ${({ theme }) => theme.palette.text.label};
`;

const AccountTypeSection = styled.div`
	margin-bottom: ${({ theme }) => theme.spacing(4)};
`;

const AccountTypeButtonsContainer = styled.div`
	display: grid;
	grid-template-columns: 1fr 1fr;
	gap: ${({ theme }) => theme.spacing(2)};
	margin-bottom: ${({ theme }) => theme.spacing(3)};
`;

const AccountTypeButton = styled.button<{ selected: boolean }>`
	background-color: ${({ theme }) => theme.palette.background.paper};
	border: 2px solid
		${({ selected, theme }) =>
			selected ? '#4285F4' : theme.palette.divider};
	border-radius: 8px;
	padding: ${({ theme }) => theme.spacing(3)};
	cursor: pointer;
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: ${({ theme }) => theme.spacing(1.5)};
	transition: all 0.2s ease-in-out;
	color: ${({ theme }) => theme.palette.text.primary};

	&:hover {
		border-color: ${({ selected }) => (selected ? '#4285F4' : '#666')};
		background-color: ${({ theme, selected }) =>
			selected
				? theme.palette.background.paper
				: theme.palette.action.hover};
	}
`;

const AccountTypeButtonText = styled.div`
	font-size: 18px;
	font-weight: 500;
	color: ${({ theme }) => theme.palette.text.primary};
`;

const AccountTypeDescription = styled.div`
	border-radius: 8px;
`;

const AccountTypeTitle = styled.h3`
	font-size: 18px;
	font-weight: 600;
	margin: 0 0 ${({ theme }) => theme.spacing(2)} 0;
	color: ${({ theme }) => theme.palette.text.primary};
`;

const DescriptionContent = styled.div`
	display: flex;
	flex-direction: column;
	gap: ${({ theme }) => theme.spacing(2)};
`;

const DescriptionSection = styled.div`
	display: flex;
	flex-direction: column;
	gap: ${({ theme }) => theme.spacing(0.5)};
`;

const DescriptionSubheading = styled.div`
	font-weight: 600;
	font-size: 14px;
	color: ${({ theme }) => theme.palette.text.primary};
`;

const DescriptionText = styled.div`
	font-size: 14px;
	color: ${({ theme }) => theme.palette.text.secondary};
	line-height: 1.5;
`;

const CreateAccountPrompt = styled.div`
	margin-top: ${({ theme }) => theme.spacing(1)};
	font-size: 14px;
	color: ${({ theme }) => theme.palette.text.secondary};
	display: flex;
	flex-direction: column;
	gap: ${({ theme }) => theme.spacing(0.5)};
`;

const CreateAccountLink = styled(Link)`
	color: #4285f4;
	font-weight: 500;
	cursor: pointer;
	&:hover {
		text-decoration: underline;
	}
`;
